import DOMPurify from 'dompurify'

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'a']
const ALLOWED_ATTR = ['href', 'target', 'rel']

function escapeHtml (text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function looksLikeHtml (raw: string): boolean {
  return /<[a-z][\s\S]*>/i.test(raw)
}

function looksLikeEncodedHtml (raw: string): boolean {
  return /&lt;\/?[a-z]/i.test(raw)
}

/** Decodeer één keer entity-encoded HTML (`&lt;p&gt;` → `<p>`). */
export function decodeHtmlEntities (raw: string): string {
  if (!raw.includes('&')) return raw
  if (typeof document === 'undefined') {
    return raw
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&amp;/gi, '&')
  }
  const textarea = document.createElement('textarea')
  textarea.innerHTML = raw
  return textarea.value
}

function normalizeRawHtmlInput (raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (looksLikeEncodedHtml(trimmed)) {
    return decodeHtmlEntities(trimmed).trim()
  }
  return trimmed
}

/** Plain text of opgeslagen HTML zonder tags. */
export function getAnnuleringsbeleidPlainText (raw: string): string {
  if (!raw.trim()) return ''
  const displayHtml = prepareAnnuleringsbeleidForDisplay(raw)
  if (!displayHtml) return ''
  if (typeof document === 'undefined') {
    return displayHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  }
  const div = document.createElement('div')
  div.innerHTML = displayHtml
  return (div.textContent ?? '').replace(/\s+/g, ' ').trim()
}

export function isAnnuleringsbeleidEmpty (raw: string | null | undefined): boolean {
  return getAnnuleringsbeleidPlainText(raw ?? '') === ''
}

/** Voor Tiptap: bestaande plain-text policies omzetten naar HTML. */
export function toEditorHtml (raw: string): string {
  const normalized = normalizeRawHtmlInput(raw)
  if (!normalized) return ''
  if (looksLikeHtml(normalized)) return normalized
  return normalized
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/** Veilige HTML voor weergave bij klanten. */
export function sanitizeAnnuleringsbeleidHtml (raw: string): string {
  if (!raw.trim()) return ''
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_ATTR: ['target', 'rel'],
  })
}

/** HTML klaar voor `dangerouslySetInnerHTML` (decode + plain-text fallback + sanitize). */
export function prepareAnnuleringsbeleidForDisplay (raw: string): string {
  const normalized = normalizeRawHtmlInput(raw)
  if (!normalized) return ''
  const html = looksLikeHtml(normalized) ? normalized : toEditorHtml(normalized)
  return sanitizeAnnuleringsbeleidHtml(html)
}

/** Lege editor-output (`<p></p>`) → leeg voor database. */
export function normalizeAnnuleringsbeleidForSave (html: string): string {
  if (isAnnuleringsbeleidEmpty(html)) return ''
  const normalized = normalizeRawHtmlInput(html)
  const prepared = looksLikeHtml(normalized) ? normalized : toEditorHtml(normalized)
  return sanitizeAnnuleringsbeleidHtml(prepared.trim())
}
